import {
  area,
  buffer,
  featureCollection,
  intersect,
  point,
  simplify,
  toMercator,
  feature as turfFeature,
  union,
} from '@turf/turf';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';

import type { GeojsonShape } from '@/common/types/util.types';

/** Geometry types that already enclose an area — usable as-is for a zonal-stats AOI. */
const AREA_GEOMETRY_TYPES = new Set(['Polygon', 'MultiPolygon']);

/** A feature with an area: what turf can union, intersect and measure. */
export type PolygonFeature = Feature<Polygon | MultiPolygon>;

/** Narrow to the geometry kinds turf can intersect. */
export const isPolygonalGeometry = (geom: Geometry | null | undefined): geom is Polygon | MultiPolygon =>
  !!geom && AREA_GEOMETRY_TYPES.has(geom.type);

/**
 * The polygonal part of `feature` that falls **inside** `aoi`, or null when the
 * two do not meet / either side is unsuitable. Keeping the clipped geometry (not
 * just its area) lets callers union overlapping parcels before measuring them —
 * essential for a coverage headline that must never exceed the AOI.
 */
export function intersectionFeature(
  aoi: PolygonFeature,
  feature: Feature<Geometry>,
): PolygonFeature | null {
  if (!isPolygonalGeometry(feature.geometry)) {
    return null;
  }
  try {
    return intersect(featureCollection([aoi, turfFeature(feature.geometry)]));
  } catch (e) {
    // Do not log the geometry itself: national ArcGIS polygons can carry hundreds
    // of thousands of coordinates, which makes one warning retain/serialize a
    // huge graph in devtools.
    console.warn('Failed to compute polygon intersection:', e);
    return null;
  }
}

/**
 * Union polygonal features into one footprint. Null for an empty list or when
 * Turf cannot union invalid upstream rings. A single feature passes through.
 */
export function unionPolygonFeatures(
  features: PolygonFeature[],
): PolygonFeature | null {
  if (!features.length) {
    return null;
  }
  if (features.length === 1) {
    return features[0];
  }
  try {
    return union(featureCollection(features));
  } catch (e) {
    console.warn('Failed to union polygon features:', e);
    return null;
  }
}

/**
 * Area in hectares of `merged` when the union succeeded, else the summed areas
 * of the pieces it was built from. Overlapping features therefore count once
 * whenever Turf could merge them, and malformed upstream rings degrade to an
 * over-count rather than to nothing — callers that know an upper bound (an AOI)
 * should clamp that rare fallback to it.
 *
 * Takes the already-merged footprint rather than re-unioning: the caller needs
 * that footprint anyway, and retrying a union that just failed on national-scale
 * geometry is the one case where it is most expensive.
 */
export function mergedAreaHa(
  merged: PolygonFeature | null,
  pieces: PolygonFeature[],
): number {
  return merged
    ? calculateAreaSizeInHa(merged)
    : pieces.reduce((sum, feature) => sum + calculateAreaSizeInHa(feature), 0);
}

/**
 * Hectares of `feature` that fall **inside** `aoi` — the clipped intersection,
 * not the feature's full extent. 0 when either side is non-polygonal, they don't
 * meet, or turf can't intersect them (a self-intersecting upstream ring).
 */
function intersectionAreaHa(aoi: PolygonFeature, feature: Feature<Geometry>): number {
  const clipped = intersectionFeature(aoi, feature);
  return clipped ? calculateAreaSizeInHa(clipped) : 0;
}

/** Total hectares of `features` falling inside `aoi` — see {@link intersectionAreaHa}. */
export function sumIntersectionAreaHa(
  aoi: PolygonFeature,
  features: Feature<Geometry>[],
): number {
  return features.reduce((sum, feature) => sum + intersectionAreaHa(aoi, feature), 0);
}

/**
 * Ensure a feature carries an *area* geometry suitable for zonal-stats / mosaic
 * AOIs (climate ERA5, satellite-index NDVI). Point / line instrument layers
 * (e.g. sporecatchers) have no area, so polygon-expecting backends drop them
 * (the climate coord extractor skips non-polygons; the mosaic endpoint returns
 * nothing). Buffer those into a small polygon around the location so a point
 * samples its immediate neighbourhood; Polygon/MultiPolygon pass through
 * unchanged. Returns the original feature if buffering fails.
 *
 * `radiusMeters` defaults to 1 m — just enough to make a valid polygon AOI while
 * staying inside a single pixel, so the sample stays a point-like reading and
 * doesn't average across neighbours as satellite resolution increases.
 */
export function ensureAreaFeature(feature: Feature, radiusMeters = 1): Feature {
  const type = feature.geometry?.type;
  if (!type || AREA_GEOMETRY_TYPES.has(type)) {
    return feature;
  }
  const buffered = buffer(feature, radiusMeters / 1000, { units: 'kilometers' });
  return buffered ?? feature;
}

export function convertBbox(bbox: [number, number, number, number]) {
  const bboxMin = toMercator(point([bbox[0], bbox[1]]));
  const bboxMax = toMercator(point([bbox[2], bbox[3]]));

  return [
    bboxMin.geometry.coordinates[0],
    bboxMin.geometry.coordinates[1],
    bboxMax.geometry.coordinates[0],
    bboxMax.geometry.coordinates[1],
  ];
}

/**
 * Hectares below which a geometry is treated as a degenerate placeholder rather
 * than a real boundary. The zones backend cannot represent a polygon *with
 * holes* and returns a ~0-area stand-in for one, which every AOI consumer has to
 * recognise — so the threshold lives with the area measurement it is compared
 * against, not per consumer.
 */
export const DEGENERATE_AREA_HA = 0.01;

export function calculateAreaSizeInHa(geojson: GeojsonShape) {
  // TiPg /items can emit rows whose geom column is NULL (observed on the
  // inverbosques landcover collection) — turf's area() crashes reading
  // `geometry.type` on those, so drop them before measuring.
  if (geojson?.type === 'FeatureCollection') {
    return area({ ...geojson, features: geojson.features.filter((f) => f?.geometry) }) / 10_000;
  }
  return area(geojson) / 10_000;
}

/**
 * Project a GeoJSON Feature's coordinates from WGS84 (EPSG:4326) into Web
 * Mercator (EPSG:3857). Used when the consumer (e.g. the time-series
 * `/cog/statistics` POST) wants to declare `coord_crs=epsg:3857` and skip
 * a server-side reproject — our airborne COGs are stored natively in 3857.
 * Deep-clones the input so the caller's selection state is left intact.
 */
export function featureToMercator<T extends GeoJSON.Feature>(feature: T): T {
  const cloned = JSON.parse(JSON.stringify(feature)) as T;
  toMercator(cloned, { mutate: true });
  return cloned;
}

/**
 * Coerce a GeojsonShape (Feature | FeatureCollection | bare Geometry) into a
 * single Feature. For multi-feature collections the polygons are unioned so
 * that downstream zonal-stats / sampling consumers see one continuous shape.
 *
 * Returns `null` when the input has no usable geometry — callers must check
 * before passing the result to anything that touches `.geometry.coordinates`.
 *
 * Why not reuse `getPolygonFromGeojson` from `layers.helper.ts`? That helper
 * needs an OL `Map` and projects into the map's CRS — we want a plain
 * EPSG:4326 GeoJSON Feature here for backend zonal-stats endpoints. Same
 * shape-normalisation logic, different output target.
 */
export function normalizeToFeature(input: unknown): Feature | null {
  if (!input || typeof input !== 'object') {return null;}
  const g = input as { type?: string; features?: unknown[]; geometry?: unknown; coordinates?: unknown };

  if (g.type === 'Feature' && g.geometry) {
    return input as Feature;
  }

  if (g.type === 'FeatureCollection' && Array.isArray(g.features) && g.features.length > 0) {
    if (g.features.length === 1) {
      return g.features[0] as Feature;
    }
    try {
      const merged = union(input as Parameters<typeof union>[0]);
      if (merged) {return merged as Feature;}
    } catch {
      // turf union can throw on invalid / mixed geometries — fall through to
      // the manual MultiPolygon assembly used in `getPolygonFromGeojson`.
    }
    const coords: number[][][][] = [];
    for (const f of g.features) {
      const feat = f as Feature | undefined;
      const geom = feat?.geometry;
      if (geom?.type === 'Polygon') {
        coords.push((geom as { coordinates: number[][][] }).coordinates);
      } else if (geom?.type === 'MultiPolygon') {
        coords.push(...(geom as { coordinates: number[][][][] }).coordinates);
      }
    }
    if (coords.length === 0) {return null;}
    return {
      type: 'Feature',
      geometry: { type: 'MultiPolygon', coordinates: coords },
      properties: {},
    };
  }

  // Bare Geometry — wrap so consumers can read `.geometry`.
  if (typeof g.type === 'string' && g.coordinates) {
    return {
      type: 'Feature',
      geometry: input as Geometry,
      properties: {},
    };
  }

  return null;
}

export function calculateAreaSizeFromGSD(gsd: number, validPixels: number): number {
  return (validPixels * gsd * gsd) / 10_000;
}

function parsePositiveAreaNumber(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  const s = String(value).trim();
  if (!s) {
    return null;
  }
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Reads server-side area from vector feature properties (`area_ha`, or `area_sqm` / 10_000).
 * Used when geometry comes from vector tiles (often simplified) so stats can match Feature Data.
 */
function parseAuthoritativeAreaHaFromProps(
  props: Record<string, unknown> | null | undefined,
): number | null {
  if (!props) {
    return null;
  }
  const areaHa = parsePositiveAreaNumber(props.area_ha);
  if (areaHa != null) {
    return areaHa;
  }
  const areaSqm = parsePositiveAreaNumber(props.area_sqm);
  return areaSqm != null ? areaSqm / 10_000 : null;
}

/**
 * Server-side area for ONE feature, or null when it publishes none.
 *
 * The single definition of "this row carries its own area", so that summing it,
 * averaging it, and testing whether a row is measurable at all cannot drift —
 * in particular they all honour `area_sqm` and comma decimals, which an ad-hoc
 * `Number(props.area_ha)` at a call site does not.
 */
export function authoritativeAreaHaFromFeature(
  feature: { properties?: unknown } | null | undefined,
): number | null {
  return parseAuthoritativeAreaHaFromProps(
    feature?.properties as Record<string, unknown> | null | undefined,
  );
}

/** Sums authoritative ha across features when each exposes `area_ha` / `area_sqm`. */
export function sumAuthoritativeAreaHaFromGeojson(geojson: GeojsonShape): number | null {
  if (!geojson || typeof geojson !== 'object' || !('type' in geojson)) {
    return null;
  }
  if (geojson.type === 'FeatureCollection') {
    let sum = 0;
    let count = 0;
    for (const f of geojson.features) {
      const ha = authoritativeAreaHaFromFeature(f);
      if (ha != null) {
        sum += ha;
        count++;
      }
    }
    return count > 0 ? sum : null;
  }
  if (geojson.type === 'Feature') {
    return authoritativeAreaHaFromFeature(geojson);
  }
  return null;
}

/**
 * Stable identity string derived from a GeoJSON geometry. Two distinct
 * geometries produce distinct strings; redrawing the exact same shape
 * produces the same string. djb2 hash over the canonical JSON form —
 * fast enough for the small handful of polygons in a draw / picker
 * session, and the deterministic output is what callers need to dedup
 * features that lack a stable `id` / `properties.id`.
 *
 * Single caller today: `<FeatureTimeSeries>` stamps these on Selection-
 * Tool drawn polygons so the chart's diff reconciler treats two
 * distinct draws as two distinct features rather than collapsing them
 * onto the `__zone_aggregate__` sentinel. Lifted to `geo.helper` since
 * the shape "drawn-from-scratch geometry that needs an identity for
 * dedup" generalises cleanly — adopt from a second caller as the need
 * arises rather than retrofitting speculatively (cross-section and
 * temporal-coloring were considered but don't have the right shape:
 * cross-section is one-shot, temporal-coloring uses BE-supplied ids).
 *
 * The `prefix` is purely cosmetic — keeps debugger inspections of
 * synthesised ids self-describing (`selection-3f9a2b1c` reads better
 * in a feature's `id` than a bare integer).
 */
export function geometryHash(geometry: unknown, prefix: string = 'geom'): string {
  const s = JSON.stringify(geometry ?? '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return `${prefix}-${(h >>> 0).toString(16)}`;
}

/**
 * Unwrap a stored `selectedGeojson` value (a `Feature`, a `FeatureCollection`,
 * or null) into a flat list of features. Shared by the FeatureTimeSeries chart
 * and the Trends panel dashboard, which both read the same store value.
 */
export function collectFeaturesFromGeojson(geojson: unknown): Feature[] {
  if (!geojson) {
    return [];
  }
  const g = geojson as { type?: string; features?: unknown[] };
  if (g.type === 'FeatureCollection' && Array.isArray(g.features)) {
    return g.features as Feature[];
  }
  if (g.type === 'Feature') {
    return [geojson as Feature];
  }
  return [];
}

/**
 * vector-tiler `/items` requests go through CloudFront, which hard-caps
 * `path + query` at 8192 bytes and answers a `414 URI Too Long` *at the edge* —
 * before WAF/origin and with no CORS headers, so the browser only ever sees an
 * opaque CORS error and there is no server-side signal to catch. It is one of the
 * few CloudFront quotas with no increase path, so the limit has to be held on the
 * client. The variable-length part is the `s_intersects` polygon filter.
 */
export const CLOUDFRONT_URL_MAX_BYTES = 8192;

/**
 * Bytes held back for params appended *after* the filter is measured — chiefly
 * the `&offset=N` the pagination walk grafts on for every page past the first.
 * Budgeting only for the first page is exactly how a filter measuring 7,299
 * bytes still 414'd on page two.
 */
const APPENDED_PARAMS_HEADROOM_BYTES = 128;

/** Byte budget for a filtered `/items` URL's path + query, before pagination params. */
export const FILTER_URL_MAX_BYTES = CLOUDFRONT_URL_MAX_BYTES - APPENDED_PARAMS_HEADROOM_BYTES;

/**
 * ASCII bytes the WHATWG URL parser percent-encodes in a query string on an
 * `http`/`https` (a "special") URL — the "special-query percent-encode set":
 * space, `"`, `#`, `'`, `<`, `>`. `,` `:` `[` `]` `{` `}` are not in it and
 * stay literal. Every C0 control (<0x20) is also encoded, which the `<= 0x1F`
 * check in {@link wirePathQueryBytes} covers directly rather than listing here.
 */
const QUERY_PERCENT_ENCODE_BYTES = new Set<number>([0x20, 0x22, 0x23, 0x27, 0x3c, 0x3e]);

/**
 * Bytes of `path + query` as the browser will actually put them on the wire.
 *
 * These URLs are handed to `axios.get(url)` as a pre-built string, so only the
 * browser's URL parser touches them, per the WHATWG URL Standard: every C0
 * control and every byte of the UTF-8 encoding above 0x7E (ASCII `~`) —
 * i.e. *every* non-ASCII character, encoded byte-by-byte — plus the
 * {@link QUERY_PERCENT_ENCODE_BYTES} set are each turned into a 3-byte `%XX`
 * triplet; everything else, including `,` `:` `[` `]` `{` `}`, stays literal.
 * Operating byte-wise on the UTF-8 encoding (rather than char-by-char) is what
 * makes a single non-ASCII character correctly cost 3 bytes per UTF-8 byte,
 * not per character — a 3-byte UTF-8 character becomes 9 wire bytes.
 *
 * The query set is applied uniformly across path AND query. The WHATWG *path*
 * set additionally encodes `` ` `` `{` `}` `?`, so a path containing those would
 * be under-counted. Not reachable through `buildItemsEndpoint`, whose path is
 * `/collections/{id}/items` with ids from our own catalog — but worth knowing
 * before reusing this on an arbitrary URL.
 *
 * Measuring the raw string under-counts by 2 bytes for every encoded byte in
 * the CQL2-JSON filter (a class name, a collection id — anything with an
 * accent or other non-ASCII character included); measuring the whole URL
 * over-counts by the scheme and host, which CloudFront does not count toward
 * the limit. An incomplete encode set here would silently let a URL past the
 * guard that still 414s at the edge — precisely the failure this budget
 * exists to catch.
 */
export function wirePathQueryBytes(url: string): number {
  // `[^/?#]` not `[^/]`: on a path-less URL (`https://host?a=1`) a `[^/]` host
  // match swallows the query too, so the whole thing measured 0 bytes and sailed
  // through any budget check.
  const pathQuery = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
  let bytes = 0;
  for (const byte of new TextEncoder().encode(pathQuery)) {
    bytes += byte <= 0x1f || byte >= 0x7f || QUERY_PERCENT_ENCODE_BYTES.has(byte) ? 3 : 1;
  }
  return bytes;
}

/**
 * Log loudly when a request URL is about to blow the edge limit.
 *
 * CloudFront answers an oversized URL with a bare 414 before any CORS-attaching
 * hook runs, so the browser reports an opaque CORS/preflight failure carrying no
 * usable detail. Without an explicit check this failure mode is near-unreadable
 * from the console, and keeps coming back disguised as a CORS bug. Returns true
 * when the URL is within budget.
 */
export function checkEdgeUrlLimit(url: string, context: string): boolean {
  const bytes = wirePathQueryBytes(url);
  if (bytes <= CLOUDFRONT_URL_MAX_BYTES) {
    return true;
  }
  console.error(
    `[${context}] request URL is ${bytes} bytes of path+query, over CloudFront's ` +
      `${CLOUDFRONT_URL_MAX_BYTES}-byte limit. The edge answers 414 before adding CORS ` +
      'headers, so this surfaces in the browser as a CORS error rather than a 414.',
  );
  return false;
}

/** ~0.11 m at the equator — finer than any parcel/tree selection needs. */
const FILTER_COORD_PRECISION = 6;

const roundCoordinates = (coords: unknown): unknown => {
  if (typeof coords === 'number') {
    const f = 10 ** FILTER_COORD_PRECISION;
    return Math.round(coords * f) / f;
  }
  return Array.isArray(coords) ? coords.map(roundCoordinates) : coords;
};

/** Geometry clone with every coordinate rounded to {@link FILTER_COORD_PRECISION} decimals. */
export function roundGeometryPrecision<T extends Geometry>(geometry: T): T {
  if (!('coordinates' in geometry)) {
    return geometry; // GeometryCollection etc. — nothing to round
  }
  return {
    ...geometry,
    coordinates: roundCoordinates((geometry as { coordinates: unknown }).coordinates),
  } as T;
}

/** CQL2-JSON `s_intersects` filter value for a geometry, coordinates rounded. */
export function intersectsFilter(geometry: Geometry): string {
  return `{"op": "s_intersects", "args": [{"property": "geom"}, ${JSON.stringify(
    roundGeometryPrecision(geometry),
  )}]}`;
}

export interface FittedFilter {
  /** CQL2-JSON `s_intersects` value to drop into `&filter=`. */
  filter: string;
  /** False when even the coarsest simplification can't fit the URL limit — caller should skip the request and ask the user to simplify. */
  fits: boolean;
  /** True when Douglas-Peucker simplification had to be applied (returned features may not be vertex-exact). */
  simplified: boolean;
}

/**
 * Produce an `s_intersects` filter that keeps `buildUrl(filter)` under CloudFront's
 * URL limit. Always rounds coordinates to 6 dp; if the URL is still too long it
 * Douglas-Peucker-simplifies the geometry with escalating tolerance until it fits.
 * `buildUrl` receives the candidate filter string and must return the *full* URL so
 * the byte budget accounts for path + all other params.
 */
export function fitIntersectsFilter(
  geometry: Geometry,
  buildUrl: (filter: string) => string,
  maxBytes: number = FILTER_URL_MAX_BYTES,
): FittedFilter {
  let filter = intersectsFilter(geometry);
  if (wirePathQueryBytes(buildUrl(filter)) <= maxBytes) {
    return { filter, fits: true, simplified: false };
  }

  // Escalating Douglas-Peucker tolerance in degrees (~1 m at 1e-5, ~100 m at 1e-3).
  for (const tolerance of [1e-5, 2e-5, 5e-5, 1e-4, 2e-4, 5e-4, 1e-3]) {
    try {
      const simplified = simplify({ type: 'Feature', geometry, properties: {} } as Feature, {
        tolerance,
        highQuality: true,
        mutate: false,
      });
      filter = intersectsFilter(simplified.geometry);
      if (wirePathQueryBytes(buildUrl(filter)) <= maxBytes) {
        return { filter, fits: true, simplified: true };
      }
    } catch {
      // turf simplify can throw on degenerate rings — keep escalating tolerance
    }
  }
  return { filter, fits: false, simplified: true };
}

export const lonLatToTile = (lon: number, lat: number, zoom: number): { x: number; y: number } => {
  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  const y = Math.floor(
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
      Math.pow(2, zoom),
  );
  return { x, y };
};

export const getCoveringTile = (
  coordinates: GeoJSON.Position | GeoJSON.Position[] | GeoJSON.Position[][],
  minZoom = 10,
  maxZoom = 18,
): { x: number; y: number; z: number } => {
  if (typeof coordinates[0] === 'number') {
    const [lon, lat] = coordinates as GeoJSON.Position;
    return { ...lonLatToTile(lon, lat, minZoom), z: minZoom };
  }

  const allCoords = (
    Array.isArray(coordinates[0][0])
      ? (coordinates as GeoJSON.Position[][])
      : [coordinates as GeoJSON.Position[]]
  ).flat(1);

  const lons = allCoords.map(([lon]) => lon);
  const lats = allCoords.map(([, lat]) => lat);

  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  for (let z = maxZoom; z >= minZoom; z--) {
    const topLeft = lonLatToTile(minLon, maxLat, z);
    const bottomRight = lonLatToTile(maxLon, minLat, z);

    const dx = bottomRight.x - topLeft.x;
    const dy = bottomRight.y - topLeft.y;

    if (dx <= 0 && dy <= 0) {
      const { x, y } = lonLatToTile(centerLon, centerLat, z);
      return { x, y, z };
    }
  }

  return { ...lonLatToTile(centerLon, centerLat, minZoom), z: minZoom };
};
